import React from "react";
import { apiRequest } from "../utils/apiClient";
import { useToken } from "../hooks/useToken";
import { useIsAuthenticated } from "@azure/msal-react";
import { SESSIE_VERLOPEN, AUTH_CONTROLE_MISLUKT } from "../utils/authMessages";

// Provides roles, pages, capabilities from backend /auth/me (matrix mode)
//
// Twee uitkomsten worden strikt gescheiden gehouden:
//  - denied: de server antwoordde en zei nee (echt autorisatie-oordeel).
//  - error:  technische fout (netwerk, timeout, 5xx, 401). Herstelbaar, en
//            nadrukkelijk GEEN uitspraak over rechten.
// Consumers checken altijd EERST `error`, dan pas `allowed`/`denied`.
export const AuthInfoContext = React.createContext({
  loading: true,
  roles: [],
  pages: [],
  capabilities: {},
  allowed: false,
  hasRequiredRole: false,
  denied: false,
  error: null,
  refresh: () => {},
});

// 403-codes die een echt autorisatie-oordeel zijn. Een 403 met onbekende code
// telt eveneens als oordeel (fail-closed op de autorisatie-as); een 401 is
// altijd technisch, want die betekent ontbrekend/verlopen token.
const AUTHZ_CODES = new Set([
  "forbidden_role",
  "invalid_audience",
  "invalid_tenant",
  "invalid_issuer",
]);

// MSAL kan kort bezig zijn (inProgress); useToken geeft dan "" terug. Zonder
// eigen retry bleef de state eeuwig op loading:true hangen.
const MAX_TOKEN_RETRIES = 5;
const tokenRetryDelay = (attempt) => Math.min(2000, 200 * 2 ** (attempt - 1));

const EMPTY = { roles: [], pages: [], capabilities: {}, allowed: false, hasRequiredRole: false };

export function AuthInfoProvider({ children }) {
  const isAuthenticated = useIsAuthenticated();
  const tokenProvider = useToken({ loginErrorMessage: SESSIE_VERLOPEN });
  const [state, setState] = React.useState({
    loading: true,
    ...EMPTY,
    denied: false,
    error: null,
  });

  const retryRef = React.useRef(0);
  const timerRef = React.useRef(null);
  const loadRef = React.useRef(null);
  // Dubbelmount (StrictMode) plus retries maken gelijktijdige load()-calls
  // reëel; alleen de nieuwste run mag de state nog schrijven.
  const runIdRef = React.useRef(0);

  const load = React.useCallback(async () => {
    const runId = ++runIdRef.current;
    const isStale = () => runId !== runIdRef.current;

    if (!isAuthenticated) {
      setState({ loading: false, ...EMPTY, denied: false, error: null });
      return;
    }
    try {
      const token = await tokenProvider();
      if (isStale()) return;
      if (!token) {
        // Re-auth bezig: plan een eigen retry i.p.v. te wachten tot de
        // useCallback-dependencies toevallig wijzigen.
        if (retryRef.current < MAX_TOKEN_RETRIES) {
          retryRef.current += 1;
          setState((s) => ({ ...s, loading: true, error: null }));
          timerRef.current = setTimeout(() => loadRef.current?.(), tokenRetryDelay(retryRef.current));
        } else {
          setState({
            loading: false,
            ...EMPTY,
            denied: false,
            error: {
              kind: "technical",
              message: AUTH_CONTROLE_MISLUKT,
              status: null,
              code: "token_unavailable",
            },
          });
        }
        return;
      }
      retryRef.current = 0;
      const res = await apiRequest("auth/me", { method: "GET", tokenProvider: async () => token, suppressAuthRedirect: true });
      if (isStale()) return;
      // apiRequest always returns { data: <payload> }. Our backend wraps in envelope { success,data,error,meta }.
      let data = res?.data;
      if (data && typeof data === 'object' && 'data' in data) {
        data = data.data; // unwrap standard envelope
      }
      // Geslaagde auth/me: reset de anti-loop-teller.
      try {
        sessionStorage.removeItem("AUTH_REDIRECT_COUNT");
        sessionStorage.removeItem("AUTH_LOOP_DETECTED");
      } catch {}
      const allowed = !!data?.allowed;
      const hasRequiredRole = !!data?.hasRequiredRole;
      setState({
        loading: false,
        roles: data?.roles || [],
        pages: data?.pages || [],
        capabilities: data?.capabilities || {},
        allowed,
        hasRequiredRole,
        denied: !allowed || !hasRequiredRole,
        error: null,
      });
    } catch (err) {
      if (isStale()) return;
      const status = err?.status ?? null;
      const code = err?.code ?? null;
      const isAuthzVerdict = status === 403 && (code == null || AUTHZ_CODES.has(code));
      if (isAuthzVerdict) {
        setState({ loading: false, ...EMPTY, denied: true, error: null });
      } else {
        // 401, 5xx, netwerk, abort, timeout: herstelbaar, geen oordeel.
        // allowed/hasRequiredRole blijven false zodat routing fail-closed blijft.
        setState({
          loading: false,
          ...EMPTY,
          denied: false,
          error: { kind: "technical", message: AUTH_CONTROLE_MISLUKT, status, code },
        });
      }
    }
  }, [isAuthenticated, tokenProvider]);

  // useMsal() levert bij elke render nieuwe identiteiten, dus `tokenProvider` en
  // daarmee `load` wisselen continu. Het effect mag daar niet op triggeren —
  // anders herlaadt elke setState opnieuw en ontstaat een oneindige lus.
  // De ref houdt altijd de nieuwste load vast; het effect draait op de
  // werkelijk betekenisvolle input (isAuthenticated).
  React.useLayoutEffect(() => {
    loadRef.current = load;
  });

  const refresh = React.useCallback(() => {
    retryRef.current = 0;
    setState((s) => ({ ...s, loading: true, error: null }));
    loadRef.current?.();
  }, []);

  React.useEffect(() => {
    loadRef.current?.();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isAuthenticated]);

  return <AuthInfoContext.Provider value={{ ...state, refresh }}>{children}</AuthInfoContext.Provider>;
}

export function useAuthInfo() {
  return React.useContext(AuthInfoContext);
}
